import { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, Save, Mail } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { GIG_PLATFORMS, VEHICLE_PREFS, DURATIONS, US_STATES } from "../lib/constants";
import { submitApplication, saveDraft, loadDraft } from "../lib/api";

const STEPS = [
  { id: 1, label: "About you" },
  { id: 2, label: "License" },
  { id: 3, label: "Your hustle" },
  { id: 4, label: "Vehicle" },
];

const initial = {
  full_name: "",
  email: "",
  phone: "",
  city: "",
  state: "",
  has_valid_license: null,
  gig_platforms: [],
  vehicle_preference: "",
  rental_duration: "",
  additional_info: "",
};

const inputBase =
  "w-full bg-white border-2 border-black px-4 py-3 font-mono text-sm rounded-none focus:outline-none focus:ring-2 focus:ring-[#E60012] focus:ring-offset-0 placeholder:text-black/30";

const Field = ({ label, children, required }) => (
  <div className="space-y-2">
    <Label className="gd-tag text-black">
      {label} {required && <span className="text-[#E60012]">*</span>}
    </Label>
    {children}
  </div>
);


export default function ApplyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const autoSavedRef = useRef(false);

  useEffect(() => {
    const token = searchParams.get("resume");
    if (!token) return;
    loadDraft(token)
      .then((d) => {
        setDraftId(d.id);
        setData((prev) => ({ ...prev, ...d.payload, email: d.email }));
        setStep(Math.min(4, Math.max(1, d.current_step || 1)));
        autoSavedRef.current = true;
        toast.success("Welcome back — your info is saved");
      })
      .catch(() => toast.error("That resume link is no longer valid"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const togglePlatform = (p) =>
    setData((d) => ({
      ...d,
      gig_platforms: d.gig_platforms.includes(p)
        ? d.gig_platforms.filter((x) => x !== p)
        : [...d.gig_platforms, p],
    }));

  const autoSave = async (currentStep) => {
    if (!data.email || !/^\S+@\S+\.\S+$/.test(data.email)) return;
    try {
      const res = await saveDraft({
        id: draftId,
        email: data.email,
        payload: data,
        current_step: currentStep,
        send_email: false,
      });
      if (!draftId) setDraftId(res.id);
    } catch (e) {
      // silent
    }
  };

  const handleSaveForLater = async () => {
    if (!data.email || !/^\S+@\S+\.\S+$/.test(data.email)) {
      toast.error("Add your email on step 1 first so we can send you the resume link");
      setStep(1);
      return;
    }
    setSavingDraft(true);
    try {
      const res = await saveDraft({
        id: draftId,
        email: data.email,
        payload: data,
        current_step: step,
        send_email: true,
      });
      if (!draftId) setDraftId(res.id);
      if (res.email_sent) {
        toast.success(`Resume link sent to ${data.email} — check your inbox`);
      } else {
        toast.success("Progress saved. Bookmark this page to come back later.");
      }
    } catch (e) {
      toast.error("Could not save. Try again in a moment.");
    } finally {
      setSavingDraft(false);
    }
  };

  const validateStep = () => {
    if (step === 1) {
      if (!data.full_name || !data.email || !data.phone || !data.city || !data.state) {
        toast.error("Fill in all required fields"); return false;
      }
      if (!/^\S+@\S+\.\S+$/.test(data.email)) { toast.error("Enter a valid email"); return false; }
    }
    if (step === 2) {
      if (data.has_valid_license === null || data.has_valid_license === undefined) {
        toast.error("Please tell us if your license is valid"); return false;
      }
      if (data.has_valid_license === false) {
        toast.error("A valid driver's license is required to apply"); return false;
      }
    }
    if (step === 3) {
      if (data.gig_platforms.length === 0) { toast.error("Pick at least one platform"); return false; }
    }
    if (step === 4) {
      if (!data.vehicle_preference || !data.rental_duration) {
        toast.error("Choose a vehicle preference and duration"); return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    const nextStep = Math.min(4, step + 1);
    setStep(nextStep);
    // Silent auto-save after leaving step 1 (email is known by then)
    if (step >= 1) autoSave(nextStep);
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      await submitApplication({ ...data, years_driving: Number(data.years_driving) || 0 });
      toast.success("Application submitted!");
      navigate("/apply/success");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F5F5F5] min-h-screen">
      <Navbar />

      <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-12 md:py-16" data-testid="apply-page">
        <Link to="/" className="gd-tag text-black/60 hover:text-black inline-flex items-center gap-2 mb-6" data-testid="back-home-link">
          <ArrowLeft size={14} /> Back to home
        </Link>

        <div className="mb-10">
          <div className="gd-tag text-[#E60012] mb-3">▌ Driver Application</div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter uppercase leading-[0.95]">
            Three minutes.<br />
            <span className="text-[#E60012]">No fluff.</span>
          </h1>
        </div>

        {/* Progress */}
        <div className="grid grid-cols-4 gap-2 mb-10" data-testid="apply-progress">
          {STEPS.map((s) => (
            <div key={s.id} className="space-y-2">
              <div
                className={`h-2 ${step >= s.id ? "bg-[#E60012]" : "bg-black/15"} border border-black`}
              ></div>
              <div className={`gd-tag ${step >= s.id ? "text-black" : "text-black/40"}`}>
                {String(s.id).padStart(2, "0")} · {s.label}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white border-2 border-black p-6 md:p-10 gd-shadow-red" data-testid="apply-form-card">
          {/* STEP 1 */}
          {step === 1 && (
            <div className="space-y-5" data-testid="step-1">
              <h2 className="font-display font-black text-2xl md:text-3xl mb-2">Tell us about you</h2>
              <Field label="Full Name" required>
                <Input className={inputBase} value={data.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Maria Rodriguez" data-testid="input-full-name" />
              </Field>
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Email" required>
                  <Input type="email" className={inputBase} value={data.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" data-testid="input-email" />
                </Field>
                <Field label="Phone" required>
                  <Input className={inputBase} value={data.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(555) 555-1234" data-testid="input-phone" />
                </Field>
              </div>
              <div className="grid md:grid-cols-3 gap-5">
                <div className="md:col-span-2">
                  <Field label="City" required>
                    <Input className={inputBase} value={data.city} onChange={(e) => set("city", e.target.value)} placeholder="Atlanta" data-testid="input-city" />
                  </Field>
                </div>
                <Field label="State" required>
                  <select className={inputBase} value={data.state} onChange={(e) => set("state", e.target.value)} data-testid="select-state">
                    <option value="">Pick one</option>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="space-y-5" data-testid="step-2">
              <h2 className="font-display font-black text-2xl md:text-3xl mb-2">Driver's license</h2>

              <div>
                <Label className="gd-tag text-black block mb-3">
                  Do you have a valid driver's license? <span className="text-[#E60012]">*</span>
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: true, label: "Yes" },
                    { value: false, label: "No" },
                  ].map((opt) => {
                    const active = data.has_valid_license === opt.value;
                    return (
                      <button
                        key={String(opt.value)}
                        type="button"
                        onClick={() => set("has_valid_license", opt.value)}
                        className={`px-6 py-6 border-2 border-black font-display font-black uppercase tracking-wider text-xl gd-snappy inline-flex items-center justify-center gap-2 ${
                          active
                            ? (opt.value ? "bg-[#E60012] text-white" : "bg-black text-white")
                            : "bg-white text-black hover:bg-black/5"
                        }`}
                        data-testid={`valid-license-${opt.value ? "yes" : "no"}`}
                      >
                        {active && <CheckCircle2 size={20} />} {opt.label}
                      </button>
                    );
                  })}
                </div>
                {data.has_valid_license === false && (
                  <p className="mt-3 text-sm text-[#E60012] font-mono" data-testid="invalid-license-warning">
                    A valid driver's license is required to rent a car with us.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div className="space-y-5" data-testid="step-3">
              <h2 className="font-display font-black text-2xl md:text-3xl mb-2">Your hustle</h2>
              <p className="text-black/60 text-sm">Pick all the platforms you drive (or want to drive) on.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {GIG_PLATFORMS.map((p) => {
                  const active = data.gig_platforms.includes(p);
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => togglePlatform(p)}
                      className={`text-left px-4 py-3 border-2 border-black font-display font-bold uppercase tracking-wide text-sm gd-snappy ${
                        active ? "bg-[#E60012] text-white" : "bg-white text-black hover:bg-black hover:text-white"
                      }`}
                      data-testid={`platform-${p.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{p}</span>
                        {active && <CheckCircle2 size={16} />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* STEP 4 */}
          {step === 4 && (
            <div className="space-y-5" data-testid="step-4">
              <h2 className="font-display font-black text-2xl md:text-3xl mb-2">Vehicle & duration</h2>
              <Field label="Vehicle Preference" required>
                <div className="space-y-2">
                  {VEHICLE_PREFS.map((v) => (
                    <label
                      key={v.value}
                      className={`flex items-center gap-3 px-4 py-3 border-2 border-black cursor-pointer gd-snappy ${
                        data.vehicle_preference === v.value ? "bg-[#0A0A0A] text-white" : "bg-white text-black hover:bg-black/5"
                      }`}
                      data-testid={`vehicle-${v.value}`}
                    >
                      <input
                        type="radio"
                        name="vehicle"
                        value={v.value}
                        checked={data.vehicle_preference === v.value}
                        onChange={(e) => set("vehicle_preference", e.target.value)}
                        className="accent-[#E60012]"
                      />
                      <span className="font-display font-bold uppercase text-sm">{v.label}</span>
                    </label>
                  ))}
                </div>
              </Field>

              <Field label="Rental Duration" required>
                <div className="grid sm:grid-cols-3 gap-3">
                  {DURATIONS.map((d) => {
                    const active = data.rental_duration === d.value;
                    return (
                      <button
                        type="button"
                        key={d.value}
                        onClick={() => set("rental_duration", d.value)}
                        className={`px-4 py-4 border-2 border-black gd-snappy font-display font-bold uppercase text-sm text-left ${
                          active ? "bg-[#E60012] text-white" : "bg-white hover:bg-black hover:text-white"
                        }`}
                        data-testid={`duration-${d.value}`}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
              </Field>

              <Field label="Anything else? (optional)">
                <Textarea
                  rows={4}
                  className={inputBase}
                  value={data.additional_info}
                  onChange={(e) => set("additional_info", e.target.value)}
                  placeholder="Tell us about your weekly hours, target earnings, or any specific car preferences."
                  data-testid="input-additional-info"
                />
              </Field>
            </div>
          )}

          {/* Nav buttons */}
          <div className="flex justify-between mt-10 pt-6 border-t-2 border-black gap-3 flex-wrap">
            <button
              onClick={back}
              disabled={step === 1}
              className="font-display font-bold uppercase tracking-wider px-6 py-3 border-2 border-black bg-white hover:bg-black hover:text-white gd-snappy disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-2"
              data-testid="btn-back"
            >
              <ArrowLeft size={16} /> Back
            </button>

            <div className="flex gap-3 flex-wrap">
              <button
                type="button"
                onClick={handleSaveForLater}
                disabled={savingDraft}
                className="font-display font-bold uppercase tracking-wider px-5 py-3 border-2 border-black bg-white text-black hover:bg-black hover:text-white gd-snappy inline-flex items-center gap-2 disabled:opacity-60 text-sm"
                data-testid="btn-save-for-later"
                title="Save your progress and get a link emailed to you"
              >
                {savingDraft ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : <><Save size={14} /> Save & continue later</>}
              </button>

              {step < 4 ? (
                <button
                  onClick={next}
                  className="font-display font-black uppercase tracking-wider px-8 py-3 bg-[#E60012] text-white border-2 border-[#E60012] hover:bg-black hover:border-black gd-snappy inline-flex items-center gap-2"
                  data-testid="btn-next"
                >
                  Next <ArrowRight size={16} />
                </button>
              ) : (
                <button
                  onClick={submit}
                  disabled={submitting}
                  className="font-display font-black uppercase tracking-wider px-8 py-3 bg-[#E60012] text-white border-2 border-[#E60012] hover:bg-black hover:border-black gd-snappy inline-flex items-center gap-2 disabled:opacity-60"
                  data-testid="btn-submit"
                >
                  {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting...</> : <>Submit Application <ArrowRight size={16} /></>}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
